import { queryOptions } from "@tanstack/react-query";

import type {
  AdminApplicationListResponse,
  ApplicantAuditReason,
  ApplicationAudit,
  ApplicationWithApplicant,
  ApplicationWithCase,
} from "@kansai-ai-hub/api-client";
import type { ApplicationListQuery } from "@kansai-ai-hub/schema";

import { apiClient } from "@/lib/api-client";
import { fetchAllPaginated } from "@/lib/fetch-all-pages";

export const applicationKeys = {
  all: ["applications"] as const,
  lists: () => [...applicationKeys.all, "list"] as const,
  // scope は admin/enterprise でレスポンス形が異なる（前者は全件・応募者実名、後者は自社のみ）。
  // 以前は filters 内の in-band `scope` 文字列だけが衝突を防いでおり、付け忘れ/偶然一致で
  // 別 scope のキャッシュを取り違える恐れがあった。scope を **構造的なキー segment** にして、
  // filters が一致しても必ず別キーになるようにする（FE-6）。
  list: (scope: "admin" | "enterprise", filters: ApplicationListQuery) =>
    [...applicationKeys.lists(), scope, filters] as const,
  mineAll: () => [...applicationKeys.all, "mine-all"] as const,
  detail: (id: string) => [...applicationKeys.all, "detail", id] as const,
  // ADMIN 向けの完全な監査結果
  auditAdmin: (id: string) => [...applicationKeys.all, "audit", "admin", id] as const,
  // MEMBER 自身向けの BLOCKED 理由のみ
  auditApplicant: (id: string) => [...applicationKeys.all, "audit", "applicant", id] as const,
} as const;

/**
 * ADMIN 向け: 応募の完全な監査結果を取得する。
 */
export function applicationAuditQueryOptions(id: string) {
  return queryOptions({
    queryKey: applicationKeys.auditAdmin(id),
    queryFn: () =>
      apiClient.get<ApplicationAudit>(`/api/v1/applications/${id}/audit`),
    staleTime: 30_000,
    // 監査が未確定 (inProgress) の間は数秒ごとに再取得し、確定したら自動で結果に切り替える
    // (事務局が手動リロードしなくても完了が反映される)。確定後はポーリング停止。
    refetchInterval: (query) => (query.state.data?.inProgress ? 4_000 : false),
  });
}

/**
 * MEMBER 自身用: AUDIT_BLOCKED 時の理由のみ取得する。
 * AUDIT_BLOCKED 以外の status で叩くと 403 が返るので、UI 側で status 判定後に有効化する。
 */
export function applicantAuditReasonQueryOptions(id: string, enabled: boolean) {
  return queryOptions({
    queryKey: applicationKeys.auditApplicant(id),
    queryFn: () =>
      apiClient.get<ApplicantAuditReason>(`/api/v1/applications/${id}/audit`),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * 自分の応募を**全件**取得する（ページ上限に縛られない）。
 * 企業ディレクトリの「応募済み」判定など、件数に依存せず全応募を見る必要がある用途に使う。
 */
export function allMyApplicationsQueryOptions() {
  return queryOptions({
    queryKey: applicationKeys.mineAll(),
    // サーバは role(MEMBER) で「自分の応募」を判定するため mine パラメータは送らない
    queryFn: () => fetchAllPaginated<ApplicationWithCase>("/api/v1/applications"),
    staleTime: 30_000,
  });
}

export function applicationDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: applicationKeys.detail(id),
    queryFn: () =>
      apiClient.get<ApplicationWithApplicant>(`/api/v1/applications/${id}`),
    staleTime: 60_000,
  });
}

/**
 * 企業向け: 自社案件への全応募（applicant 情報付き）。
 * 画面側で案件ごとにグルーピングするため、サーバのページ境界で分断されないよう全件取得する。
 */
export function enterpriseApplicationsQueryOptions(filters: ApplicationListQuery) {
  return queryOptions({
    queryKey: applicationKeys.list("enterprise", filters),
    queryFn: () =>
      fetchAllPaginated<ApplicationWithApplicant>("/api/v1/applications", { ...filters }),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
}

/**
 * 事務局向け: 全応募一覧。画面側で企業→案件にグルーピングするため、
 * サーバのページ境界で同一案件の応募が分断されないよう全件取得する。
 */
export function adminApplicationsQueryOptions(filters: ApplicationListQuery) {
  return queryOptions({
    queryKey: applicationKeys.list("admin", filters),
    queryFn: (): Promise<AdminApplicationListResponse> =>
      fetchAllPaginated<ApplicationWithApplicant>("/api/v1/applications", { ...filters }),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
    // AI 監査中 (PENDING_AUDIT) の行がある間だけ短間隔でポーリングし、確定後の status
    // (NEW / 不適合) に自動で切り替える。確定後は false で停止。
    refetchInterval: (query) =>
      query.state.data?.items.some((a) => a.status === "PENDING_AUDIT") ? 5000 : false,
  });
}
